import { useState } from "react";
import { Link } from "react-router-dom";
import { Flex, Grid, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  type StatusTone,
} from "@/components/ui";
import type {
  DataRequestDto,
  DataRequestStatus,
  DataRequestType,
  DataRequestVia,
  NoteDto,
  PlayProfileDto,
  UserDto,
  UserOverviewResponse,
} from "../types";
import {
  bookingStatusTone,
  formatDate,
  formatDateTime,
  formatLabel,
  formatMoney,
  tierName,
  timeAgo,
} from "../labels";

/* ── Player profile ─────────────────────────────────────────────────────── */

/**
 * Everything Play collected from this person at sign-up, onboarding and Edit
 * profile. If a field is blank here it is blank in Play — nothing is invented.
 */
export function PlayProfileCard({
  user,
  profile,
}: {
  user: UserDto;
  profile: PlayProfileDto | null;
}) {
  if (!profile) {
    return (
      <Card header={<Text role="title" as="h3">Player profile</Text>}>
        <Text role="small" color="secondary" as="p">
          This account has no Play profile. It was created by HQ or by a guest
          booking and the person has not signed up to the app yet.
        </Text>
      </Card>
    );
  }
  const skillFor = (sport: string) => profile.sport_skills[sport] ?? null;
  return (
    <Card header={<Text role="title" as="h3">Player profile</Text>}>
      <Stack gap={16}>
        <Flex gap={16} align="center" wrap>
          <Avatar
            name={profile.name || profile.handle}
            {...(profile.avatar ? { src: profile.avatar } : {})}
            style={{ width: 64, height: 64 }}
          />
          <Stack gap={4}>
            <Flex gap={8} align="center" wrap>
              <Text role="bodyStrong" as="span">
                {profile.name || user.display_name || "Unnamed"}
              </Text>
              {profile.verified ? <Badge tone="success">Verified</Badge> : null}
              <Badge tone={profile.tier > 0 ? "accent" : "neutral"}>
                {tierName(profile.tier)} tier
              </Badge>
            </Flex>
            <Text role="small" color="secondary" as="span">
              @{profile.handle}
            </Text>
          </Stack>
        </Flex>

        {profile.bio ? (
          <Text role="body" as="p">
            {profile.bio}
          </Text>
        ) : null}

        <Grid columns="repeat(auto-fit, minmax(200px, 1fr))" gap={12}>
          <Field label="First name" value={profile.first_name} />
          <Field label="Last name" value={profile.last_name} />
          <Field label="Location" value={profile.location} />
          <Field label="Home court" value={profile.home_court} />
          <Field label="Joined Play" value={formatDate(profile.created_at)} />
          <Field
            label="Onboarding"
            value={
              profile.onboarding_completed_at
                ? `Completed ${formatDate(profile.onboarding_completed_at)}`
                : "Not finished"
            }
          />
        </Grid>

        <Stack gap={8}>
          <Text role="label" as="span">
            Sports and skill level
          </Text>
          {profile.sports.length === 0 ? (
            <Text role="small" color="secondary" as="p">
              No sports picked yet (onboarding skipped or not finished).
            </Text>
          ) : (
            <Flex gap={8} wrap>
              {profile.sports.map((sport, index) => (
                <Badge key={sport} tone={index === 0 ? "accent" : "info"}>
                  {sport}
                  {skillFor(sport) ? ` · ${skillFor(sport)}` : ""}
                  {index === 0 ? " (main)" : ""}
                </Badge>
              ))}
            </Flex>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Stack gap={4}>
      <Text role="caption" color="muted" as="span">
        {label}
      </Text>
      <Text role="body" as="span">
        {value && value.trim() ? value : "—"}
      </Text>
    </Stack>
  );
}

/* ── Stats ──────────────────────────────────────────────────────────────── */

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <Card>
      <Stack gap={4}>
        <Text role="caption" color="muted" as="span">
          {label}
        </Text>
        <Text role="h2" as="span">
          {value}
        </Text>
        {hint ? (
          <Text role="caption" color="secondary" as="span">
            {hint}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}

export function UserStats({ stats }: { stats: UserOverviewResponse["stats"] }) {
  const paid = Object.entries(stats.paid_by_currency);
  const active = Object.entries(stats.bookings_by_status)
    .map(([status, n]) => `${n} ${formatLabel(status)}`)
    .join(", ");
  return (
    <Grid columns="repeat(auto-fit, minmax(160px, 1fr))" gap={12}>
      <Stat label="Bookings" value={stats.bookings_total} {...(active ? { hint: active } : {})} />
      <Stat
        label="Paid (gross)"
        value={paid.length === 0 ? "—" : paid.map(([c, a]) => formatMoney(a, c)).join(" · ")}
        hint="Successful booking payments"
      />
      <Stat
        label="Communities"
        value={stats.communities_joined}
        hint={stats.communities_owned ? `${stats.communities_owned} owned` : "none owned"}
      />
      <Stat label="Posts" value={stats.posts} />
      <Stat label="Replays" value={stats.replays} />
      <Stat
        label="Reports"
        value={`${stats.reports_against} against`}
        hint={`${stats.reports_made} filed by them`}
      />
    </Grid>
  );
}

/* ── Activity tables ────────────────────────────────────────────────────── */

export function BookingsPanel({ bookings, total }: { bookings: UserOverviewResponse["bookings"]; total: number }) {
  if (bookings.length === 0) {
    return (
      <EmptyState
        title="No bookings"
        description="This person has not booked anything yet."
        reason="none"
      />
    );
  }
  return (
    <Stack gap={8}>
      <Table>
        <Thead>
          <Tr>
            <Th>Booking</Th>
            <Th>Venue</Th>
            <Th>Court / resource</Th>
            <Th>Starts</Th>
            <Th>Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {bookings.map((b) => (
            <Tr key={b.id}>
              <Td>
                <Link to={`/app/bookings/${b.id}`}>{b.public_id}</Link>
              </Td>
              <Td>
                {b.business_id ? (
                  <Link to={`/app/businesses/${b.business_id}`}>
                    {b.business_name ?? "Business"}
                  </Link>
                ) : (
                  "—"
                )}
              </Td>
              <Td>{b.resource_name ?? "—"}</Td>
              <Td>{formatDateTime(b.starts_at)}</Td>
              <Td>
                <StatusChip tone={bookingStatusTone(b.status)}>{formatLabel(b.status)}</StatusChip>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {total > bookings.length ? (
        <Text role="caption" color="muted" as="p">
          Showing the {bookings.length} most recent of {total}.
        </Text>
      ) : null}
    </Stack>
  );
}

export function CommunitiesPanel({ communities }: { communities: UserOverviewResponse["communities"] }) {
  if (communities.length === 0) {
    return (
      <EmptyState
        title="Not in any community"
        description="Communities they join in Play appear here."
        reason="none"
      />
    );
  }
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Community</Th>
          <Th>Role</Th>
          <Th>Joined</Th>
        </Tr>
      </Thead>
      <Tbody>
        {communities.map((c) => (
          <Tr key={c.club_id}>
            <Td>
              <Link to={`/app/communities/${c.club_id}`}>{c.name ?? c.club_id}</Link>
              {c.handle ? (
                <Text role="caption" color="muted" as="span">
                  {" "}
                  @{c.handle}
                </Text>
              ) : null}
              {c.archived ? <Badge tone="neutral">archived</Badge> : null}
            </Td>
            <Td>{c.role}</Td>
            <Td>{formatDate(c.joined_at)}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

export function BusinessesPanel({ businesses }: { businesses: UserOverviewResponse["businesses"] }) {
  if (businesses.length === 0) {
    return (
      <EmptyState
        title="No business roles"
        description="They neither own nor work for a business on HUDDL."
        reason="none"
      />
    );
  }
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Business</Th>
          <Th>Their role</Th>
          <Th>Role status</Th>
          <Th>Business status</Th>
        </Tr>
      </Thead>
      <Tbody>
        {businesses.map((b) => (
          <Tr key={`${b.business_id}-${b.relationship_type}`}>
            <Td>
              <Link to={`/app/businesses/${b.business_id}`}>{b.name ?? b.business_id}</Link>
            </Td>
            <Td>{formatLabel(b.relationship_type)}</Td>
            <Td>{formatLabel(b.status)}</Td>
            <Td>{b.business_status ? formatLabel(b.business_status) : "—"}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

/**
 * Account referrals, both directions: the code this account signed up with,
 * and the accounts that signed up with this one's code. Written once at sign-up.
 */
export function ReferralsPanel({ referral }: { referral: NonNullable<UserOverviewResponse["referral"]> }) {
  return (
    <Stack gap={12}>
      <Grid columns="repeat(auto-fit, minmax(220px, 1fr))" gap={12}>
        <Card>
          <Stack gap={4}>
            <Text role="caption" color="muted" as="p">Signed up with code</Text>
            <Text role="bodyStrong" as="p">{referral.code_used ? `@${referral.code_used}` : "None"}</Text>
            {referral.referred_by ? (
              <Text role="small" as="p">
                Referred by{" "}
                <Link to={`/app/users/${referral.referred_by.user_id}`}>
                  {referral.referred_by.name || `@${referral.referred_by.handle ?? referral.code_used}`}
                </Link>
                {referral.referred_at ? ` · ${formatDate(referral.referred_at)}` : ""}
              </Text>
            ) : null}
          </Stack>
        </Card>
        <Card>
          <Stack gap={4}>
            <Text role="caption" color="muted" as="p">Accounts referred</Text>
            <Text role="bodyStrong" as="p">{referral.referred_count}</Text>
            <Text role="small" color="secondary" as="p">Accounts created with this user&rsquo;s code.</Text>
          </Stack>
        </Card>
      </Grid>
      {referral.referred_accounts.length === 0 ? (
        <EmptyState
          title="No referred accounts"
          description="Nobody has signed up with this user's referral code yet."
          reason="none"
        />
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Account</Th>
              <Th>Joined with the code</Th>
            </Tr>
          </Thead>
          <Tbody>
            {referral.referred_accounts.map((a) => (
              <Tr key={a.user_id}>
                <Td>
                  <Link to={`/app/users/${a.user_id}`}>{a.name || (a.handle ? `@${a.handle}` : a.user_id)}</Link>
                  {a.handle && a.name ? (
                    <Text role="caption" color="muted" as="div">@{a.handle}</Text>
                  ) : null}
                </Td>
                <Td>{a.referred_at ? formatDate(a.referred_at) : "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
      {referral.referred_count > referral.referred_accounts.length ? (
        <Text role="caption" color="muted" as="p">
          Showing the newest {referral.referred_accounts.length} of {referral.referred_count}.
        </Text>
      ) : null}
    </Stack>
  );
}

function reportTone(status: string): StatusTone {
  if (status === "resolved" || status === "closed") return "neutral";
  return "warning";
}

export function ReportsPanel({
  made,
  against,
}: {
  made: UserOverviewResponse["reports_made"];
  against: UserOverviewResponse["reports_against"];
}) {
  const render = (title: string, list: typeof made, empty: string) => (
    <Stack gap={8}>
      <Text role="bodyStrong" as="p">
        {title}
      </Text>
      {list.length === 0 ? (
        <Text role="small" color="secondary" as="p">
          {empty}
        </Text>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Report</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Filed</Th>
            </Tr>
          </Thead>
          <Tbody>
            {list.map((r) => (
              <Tr key={r.id}>
                <Td>
                  <Link to={`/app/trust-safety/reports/${r.id}`}>{r.id.slice(0, 8)}</Link>
                </Td>
                <Td>{formatLabel(r.report_type)}</Td>
                <Td>
                  <StatusChip tone={reportTone(r.status)}>{formatLabel(r.status)}</StatusChip>
                </Td>
                <Td>{formatDateTime(r.submitted_at)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Stack>
  );
  return (
    <Stack gap={16}>
      {render("Reports against this person", against, "Nobody has reported this person.")}
      {render("Reports they filed", made, "They have not reported anyone.")}
    </Stack>
  );
}

export function EnforcementPanel({ rows }: { rows: UserOverviewResponse["enforcement"] }) {
  if (rows.length === 0) {
    return (
      <Text role="small" color="secondary" as="p">
        No enforcement actions on record.
      </Text>
    );
  }
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Action</Th>
          <Th>Status</Th>
          <Th>From</Th>
          <Th>Until</Th>
          <Th>Reason</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((e) => (
          <Tr key={e.id}>
            <Td>{formatLabel(e.action_type)}</Td>
            <Td>{formatLabel(e.status)}</Td>
            <Td>{formatDate(e.effective_from)}</Td>
            <Td>{e.effective_to ? formatDate(e.effective_to) : "open-ended"}</Td>
            <Td>{e.reason ?? "—"}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

export function TicketsPanel({ rows }: { rows: UserOverviewResponse["tickets"] }) {
  if (rows.length === 0) {
    return (
      <Text role="small" color="secondary" as="p">
        No support tickets.
      </Text>
    );
  }
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Ticket</Th>
          <Th>Reason</Th>
          <Th>Queue</Th>
          <Th>Status</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((t) => (
          <Tr key={t.id}>
            <Td>
              <Link to={`/app/support/tickets/${t.id}`}>{t.public_id}</Link>
            </Td>
            <Td>{t.contact_reason ?? "—"}</Td>
            <Td>{formatLabel(t.queue)}</Td>
            <Td>{formatLabel(t.status)}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

/* ── Notes ──────────────────────────────────────────────────────────────── */

export function NotesPanel({
  notes,
  busy,
  error,
  onAdd,
  onPin,
}: {
  notes: NoteDto[];
  busy: boolean;
  error: string | null;
  onAdd: (body: string) => Promise<boolean>;
  onPin: (note: NoteDto) => void;
}) {
  const [draft, setDraft] = useState("");
  return (
    <Stack gap={16}>
      <Stack gap={8}>
        <Textarea
          label="Add an internal note"
          hint="Only HUDDL staff can see notes. They cannot be edited or deleted."
          value={draft}
          rows={3}
          maxLength={4000}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Flex justify="flex-end">
          <Button
            loading={busy}
            disabled={draft.trim().length === 0}
            onClick={() => {
              void onAdd(draft).then((ok) => {
                if (ok) setDraft("");
              });
            }}
          >
            Add note
          </Button>
        </Flex>
        {error ? (
          <Alert tone="danger" title="Note not saved">
            {error}
          </Alert>
        ) : null}
      </Stack>
      {notes.length === 0 ? (
        <EmptyState
          title="No notes yet"
          description="Leave context for the next person who opens this profile."
          reason="none"
        />
      ) : (
        <Stack gap={8}>
          {notes.map((note) => (
            <Card key={note.id}>
              <Stack gap={8}>
                <Flex justify="space-between" align="center" wrap gap={8}>
                  <Text role="caption" color="muted" as="span">
                    {note.author?.name ?? "Former staff member"} · {formatDateTime(note.created_at)}
                    {note.pinned ? " · pinned" : ""}
                  </Text>
                  <Button size="sm" variant="ghost" onClick={() => onPin(note)}>
                    {note.pinned ? "Unpin" : "Pin"}
                  </Button>
                </Flex>
                <Text role="body" as="p" style={{ whiteSpace: "pre-wrap" }}>
                  {note.body}
                </Text>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/* ── Privacy: data-subject requests ─────────────────────────────────────── */

const REQUEST_TYPES: Array<{ value: DataRequestType; label: string; hint: string }> = [
  { value: "access", label: "Access / export", hint: "They asked for a copy of their data." },
  { value: "erasure", label: "Erasure", hint: "They asked to delete their data." },
  { value: "correction", label: "Correction", hint: "They asked to fix inaccurate data." },
  { value: "objection", label: "Objection", hint: "They object to how their data is processed." },
];

const REQUEST_VIA: Array<{ value: DataRequestVia; label: string }> = [
  { value: "email", label: "Email" },
  { value: "support_ticket", label: "Support ticket" },
  { value: "in_app", label: "In the app" },
  { value: "other", label: "Other" },
];

function requestTone(status: DataRequestStatus): StatusTone {
  switch (status) {
    case "completed":
      return "success";
    case "rejected":
      return "neutral";
    case "in_progress":
      return "info";
    default:
      return "warning";
  }
}

export function DataRequestsPanel({
  requests,
  busy,
  error,
  onCreate,
  onUpdate,
  onExport,
  exporting,
}: {
  requests: DataRequestDto[];
  busy: boolean;
  error: string | null;
  onCreate: (input: {
    request_type: DataRequestType;
    received_via: DataRequestVia;
    due_at?: string | null;
  }) => Promise<boolean>;
  onUpdate: (
    id: string,
    status: "in_progress" | "completed" | "rejected",
    note: string | null,
  ) => Promise<boolean>;
  onExport: () => void;
  exporting: boolean;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [type, setType] = useState<DataRequestType>("access");
  const [via, setVia] = useState<DataRequestVia>("email");
  const [due, setDue] = useState("");
  const [resolve, setResolve] = useState<{
    id: string;
    status: "completed" | "rejected";
  } | null>(null);
  const [note, setNote] = useState("");

  return (
    <Stack gap={16}>
      <Alert tone="info" title="Data Privacy Act requests">
        A person can ask for a copy of their data, correction, erasure or to
        object to processing (RA 10173). Log each request when it arrives, work
        it, and record the outcome. Downloading the export below is audited.
      </Alert>
      <Flex gap={8} wrap>
        <Button onClick={() => setCreateOpen(true)}>Log a request</Button>
        <Button variant="secondary" loading={exporting} onClick={onExport}>
          Download data export (JSON)
        </Button>
      </Flex>
      {error ? (
        <Alert tone="danger" title="Request failed">
          {error}
        </Alert>
      ) : null}
      {requests.length === 0 ? (
        <EmptyState
          title="No data requests"
          description="Nothing has been logged for this person."
          reason="none"
        />
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Type</Th>
              <Th>Received</Th>
              <Th>Status</Th>
              <Th>Outcome</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {requests.map((r) => (
              <Tr key={r.id}>
                <Td>
                  {formatLabel(r.request_type)}
                  <Text role="caption" color="muted" as="div">
                    via {formatLabel(r.received_via)}
                  </Text>
                </Td>
                <Td>{formatDateTime(r.received_at)}</Td>
                <Td>
                  <StatusChip tone={requestTone(r.status)}>{formatLabel(r.status)}</StatusChip>
                </Td>
                <Td>
                  {r.resolution_note ?? "—"}
                  {r.handled_by ? (
                    <Text role="caption" color="muted" as="div">
                      {r.handled_by.name ?? "Staff"} · {formatDate(r.resolved_at)}
                    </Text>
                  ) : null}
                </Td>
                <Td>
                  {r.status === "completed" || r.status === "rejected" ? (
                    "—"
                  ) : (
                    <Flex gap={4} wrap>
                      {r.status === "received" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => void onUpdate(r.id, "in_progress", null)}
                        >
                          Start
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => {
                          setNote("");
                          setResolve({ id: r.id, status: "completed" });
                        }}
                      >
                        Complete
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          setNote("");
                          setResolve({ id: r.id, status: "rejected" });
                        }}
                      >
                        Decline
                      </Button>
                    </Flex>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Log a data request"
        actions={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              loading={busy}
              onClick={() => {
                void onCreate({
                  request_type: type,
                  received_via: via,
                  ...(due ? { due_at: new Date(`${due}T23:59:59`).toISOString() } : {}),
                }).then((ok) => {
                  if (ok) setCreateOpen(false);
                });
              }}
            >
              Log request
            </Button>
          </>
        }
      >
        <Stack gap={16}>
          <Select
            label="Request type"
            value={type}
            onChange={(event) => setType(event.target.value as DataRequestType)}
            options={REQUEST_TYPES.map((t) => ({ value: t.value, label: t.label }))}
          />
          <Text role="small" color="secondary" as="p">
            {REQUEST_TYPES.find((t) => t.value === type)?.hint}
          </Text>
          <Select
            label="Received via"
            value={via}
            onChange={(event) => setVia(event.target.value as DataRequestVia)}
            options={REQUEST_VIA}
          />
          <label>
            <Text role="label" as="span">
              Respond by (optional)
            </Text>
            <input
              type="date"
              value={due}
              onChange={(event) => setDue(event.target.value)}
              style={{
                display: "block",
                width: "100%",
                marginTop: "var(--space-4)",
                padding: "var(--space-8) var(--space-12)",
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--color-border-default)",
                background: "var(--color-surface-raised)",
              }}
            />
          </label>
          {error ? (
            <Alert tone="danger" title="Not logged">
              {error}
            </Alert>
          ) : null}
        </Stack>
      </Dialog>

      <Dialog
        open={resolve !== null}
        onClose={() => setResolve(null)}
        title={resolve?.status === "completed" ? "Complete request" : "Decline request"}
        actions={
          <>
            <Button variant="secondary" onClick={() => setResolve(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              loading={busy}
              disabled={note.trim().length < 3}
              onClick={() => {
                if (!resolve) return;
                void onUpdate(resolve.id, resolve.status, note.trim()).then((ok) => {
                  if (ok) setResolve(null);
                });
              }}
            >
              Confirm
            </Button>
          </>
        }
      >
        <Stack gap={12}>
          <Textarea
            label={
              resolve?.status === "completed"
                ? "What was done"
                : "Why it is being declined"
            }
            value={note}
            onChange={(event) => setNote(event.target.value)}
            required
          />
          {error ? (
            <Alert tone="danger" title="Not saved">
              {error}
            </Alert>
          ) : null}
        </Stack>
      </Dialog>
    </Stack>
  );
}

/* ── Timeline ───────────────────────────────────────────────────────────── */

export function TimelinePanel({ rows }: { rows: UserOverviewResponse["timeline"] }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No staff actions yet"
        description="Every account, role, verification and profile change made in HQ is recorded here."
        reason="none"
      />
    );
  }
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>When</Th>
          <Th>What</Th>
          <Th>By</Th>
          <Th>Reason</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((r) => (
          <Tr key={r.id}>
            <Td>
              {formatDateTime(r.occurred_at)}
              <Text role="caption" color="muted" as="div">
                {timeAgo(r.occurred_at)}
              </Text>
            </Td>
            <Td>{formatLabel(r.action.replace(/^user\./, "").replaceAll(".", " · "))}</Td>
            <Td>{r.actor?.name ?? "System"}</Td>
            <Td>{r.reason ?? "—"}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
